'use client';

import type { PropsWithChildren } from 'react';

import { useEffect, useRef, useState } from 'react';
import { HEADER_STUCK_OFFSET } from './header-config';
import { HeaderDrawerProvider } from './header-drawer-store';

/**
 * Sticky frame of the header. From 901 px it sticks 36 px above the viewport, so the top bar
 * scrolls away while the rest stays pinned. Hiding the bar instead would change the header height
 * and make the browser's scroll anchoring bounce the page between the two states.
 */
export function HeaderShell({ children }: PropsWithChildren) {
  const headerRef = useRef<HTMLElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const updateStuck = () => setIsStuck(window.scrollY > HEADER_STUCK_OFFSET);

    updateStuck();
    window.addEventListener('scroll', updateStuck, { passive: true });
    return () => window.removeEventListener('scroll', updateStuck);
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const root = document.documentElement;
    // Anchor jumps and keyboard focus have to land below the pinned part of the header.
    const updateScrollPadding = () => {
      const pinnedHeight = header.offsetHeight + parseFloat(getComputedStyle(header).top);
      root.style.scrollPaddingTop = `${pinnedHeight}px`;
    };
    const observer = new ResizeObserver(updateScrollPadding);

    observer.observe(header);
    return () => {
      observer.disconnect();
      root.style.removeProperty('scroll-padding-top');
    };
  }, []);

  return (
    <HeaderDrawerProvider>
      <header
        ref={headerRef}
        data-stuck={isStuck || undefined}
        className="ea-header sticky top-0 z-50 bg-white text-[#14181c] shadow-[0_1px_0_#e6eaee] data-[stuck]:shadow-[0_8px_24px_-12px_rgba(0,0,0,.18)] min-[901px]:-top-9"
      >
        {children}
      </header>
    </HeaderDrawerProvider>
  );
}
